import React, { ReactNode, useEffect, useState } from 'react';

import { Scrollbar } from '@/components/Scrollbar';
import { ComingSoon } from '@/components/shared/ComingSoon';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { Fade, Slide } from '@material-ui/core';
import { pathOr } from 'ramda';

import { SettingChartRepository } from './SettingChartRepository';
import styles from './SongsSettingsPanel.module.scss';
import { SongsSettingsRoot } from './SongsSettingsRoot';

export enum ESongsSettingsDisplayState {
	ROOT,
	CHART_REPOSITORY,
	IMPORT_SONGLIST,
	CUSTOM_SONGS,
}

interface IProps {
	open: boolean;
	setOpen: (open: boolean) => void;
}

export interface IPropsSongsSettingsDisplay {
	pushDisplayState: (displayState: ESongsSettingsDisplayState) => void;
	popDisplayState: () => void;
	setOpen: (open: boolean) => void;
	panelTransitionTimeout: number;
}

const displayStateComponents: Map<
	ESongsSettingsDisplayState,
	{
		title?: string;
		component: React.FC<IPropsSongsSettingsDisplay>;
	}
> = new Map([
	[ESongsSettingsDisplayState.ROOT, { component: SongsSettingsRoot }],
	[
		ESongsSettingsDisplayState.CHART_REPOSITORY,
		{
			title: 'Chart repository',
			component: SettingChartRepository,
		},
	],
	[
		ESongsSettingsDisplayState.IMPORT_SONGLIST,
		{
			component: ComingSoon,
		},
	],
	[
		ESongsSettingsDisplayState.CUSTOM_SONGS,
		{
			component: ComingSoon,
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());

export const SongsSettingsPanel: React.FC<IProps> = (props) => {
	const [displayStateStack, setDisplayStateStack] = useState<
		ESongsSettingsDisplayState[]
	>([ESongsSettingsDisplayState.ROOT]);
	const [displayState, setDisplayState] =
		useState<ESongsSettingsDisplayState>(
			getDisplayState(displayStateStack),
		);

	const panelTitle = getNameForDisplayState(displayState);

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	return (
		<AsaphDialog open={props.open} onClose={handleClose}>
			<div className={styles['songs-settings-panel__toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: displayStateStack.length > 1,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn: !!panelTitle,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: panelTitle,
					}}
					trailingButton={{
						needsFadeIn:
							displayState === ESongsSettingsDisplayState.ROOT,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderEnd(),
					}}
				/>
			</div>
			{displayState !== ESongsSettingsDisplayState.ROOT && (
				<div
					className={styles['songs-settings-panel__toolbar-divider']}
				>
					<hr />
				</div>
			)}
			<section className={styles['songs-settings-panel__content']}>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;
					if (key === ESongsSettingsDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<Scrollbar
									className={
										styles[
											'songs-settings-panel__content-container'
										]
									}
								>
									<Component
										pushDisplayState={pushDisplayState}
										popDisplayState={popDisplayState}
										setOpen={props.setOpen}
										panelTransitionTimeout={
											PANEL_TRANSITION_TIMEOUT
										}
									/>
								</Scrollbar>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<Scrollbar
								className={
									styles[
										'songs-settings-panel__content-container'
									]
								}
								// Needed to prevent underlying Components' absolute positioned elements be on top of new Component
								style={{
									zIndex: displayStateStack.length,
								}}
							>
								<Component
									pushDisplayState={pushDisplayState}
									popDisplayState={popDisplayState}
									setOpen={props.setOpen}
									panelTransitionTimeout={
										PANEL_TRANSITION_TIMEOUT
									}
								/>
							</Scrollbar>
						</Slide>
					);
				})}
			</section>
		</AsaphDialog>
	);

	function pushDisplayState(newState: ESongsSettingsDisplayState) {
		setDisplayStateStack([...displayStateStack, newState]);
	}

	function popDisplayState() {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
		}
	}

	function handleClose() {
		props.setOpen(false);
		popDisplayState();
	}

	function renderHeaderStart(): ReactNode {
		switch (displayState) {
			case ESongsSettingsDisplayState.CHART_REPOSITORY:
				return (
					<AsaphDialogHeaderButton
						type="icon"
						label="Back"
						handlePress={popDisplayState}
					/>
				);
			case ESongsSettingsDisplayState.IMPORT_SONGLIST:
			case ESongsSettingsDisplayState.CUSTOM_SONGS:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Cancel"
						handlePress={popDisplayState}
					/>
				);
			default:
				return null;
		}
	}

	function renderHeaderEnd(): ReactNode {
		switch (displayState) {
			case ESongsSettingsDisplayState.ROOT:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Done"
						handlePress={handleClose}
					/>
				);
			default:
				return null;
		}
	}
};

function getDisplayState(displayStateStack: ESongsSettingsDisplayState[]) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: ESongsSettingsDisplayState.ROOT;
}

function getNameForDisplayState(
	displayState: ESongsSettingsDisplayState,
): string {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		return pathOr('', ['title'], displayStateComponent);
	}
	return '';
}
